/**
 * Dots & Boxes Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → line drawing →
 * box completion → extra turns → game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + score display + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { AISpeedSlider } from '../../components/AISpeedSlider';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { DotsBoxesEngine } from '../DotsBoxesEngine';
import { DotsBoxesAI } from '../DotsBoxesAI';
import { DotsBoxesBoard } from './DotsBoxesBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { DotsBoxesErrorHandler } from '../services/DotsBoxesErrorHandler';
import { DotsBoxesStatisticsService } from '../services/DotsBoxesStatisticsService';
import { DotsBoxesGamePersistence } from '../services/DotsBoxesGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import UniversalGameLayout, { UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import {
  DotsBoxesGameSettings,
  DotsBoxesGameState,
  PlayerColor,
  Line,
  DotsBoxesGameProps,
  PLAYER_COLORS,
} from '../types';
import './DotsBoxesGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const PLAYER_COLOR_DISPLAY: Record<PlayerColor, { emoji: string; name: string }> = {
  blue: { emoji: '🔵', name: 'Blue' },
  red: { emoji: '🔴', name: 'Red' },
  green: { emoji: '🟢', name: 'Green' },
  orange: { emoji: '🟠', name: 'Orange' },
};

const DotsBoxesGameInner: React.FC<DotsBoxesGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<DotsBoxesGameSettings | null>(null);
  const [gameState, setGameState] = useState<DotsBoxesGameState | null>(null);
  const [aiSpeedDelay, setAiSpeedDelay] = useState(500);

  // Refs for callbacks to avoid stale closures
  const engineRef = useRef<DotsBoxesEngine>(new DotsBoxesEngine());
  const aiRef = useRef<DotsBoxesAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const settingsRef = useRef<DotsBoxesGameSettings | null>(null);
  const aiSpeedDelayRef = useRef(500);

  // Services (lazy init)
  const errorHandlerRef = useRef<DotsBoxesErrorHandler | null>(null);
  const statsServiceRef = useRef<DotsBoxesStatisticsService | null>(null);
  const persistenceRef = useRef<DotsBoxesGamePersistence | null>(null);

  // Keep refs in sync
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new DotsBoxesErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new DotsBoxesStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new DotsBoxesGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Check for saved game on mount
  useEffect(() => {
    if (persistenceRef.current?.hasAutoSave()) {
      addNotification({
        type: NotificationType.INFO,
        title: 'Saved Game Found',
        message: 'A saved game was found. Start a new game to continue.',
        duration: 4000,
      });
    }
  }, [addNotification]);

  const handleAiSpeedChange = useCallback((delay: number) => {
    setAiSpeedDelay(delay);
    aiSpeedDelayRef.current = delay;
  }, []);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: DotsBoxesGameSettings) => {
      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      settingsRef.current = newSettings;
      setSettings(newSettings);
      setGameState(engine.getGameState());
      setView('playing');

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('dots-and-boxes');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new DotsBoxesAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.mode === 'singleplayer' ? 'Single player' : 'Multiplayer'} game started!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleLineClick = useCallback(
    (line: Line) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;

      const currentSettings = settingsRef.current;

      // In single player, only allow human (player index 0 = blue) to click
      if (currentSettings?.mode === 'singleplayer') {
        if (engine.getCurrentPlayerIndex() !== 0) return;
      }

      executeLine(line);
    },
    []
  );

  const executeLine = useCallback(
    (line: Line) => {
      const engine = engineRef.current;

      try {
        const currentPlayer = engine.getCurrentPlayer();
        const boxesCompleted = engine.drawLine(line);
        const newState = engine.getGameState();
        setGameState(newState);

        if (boxesCompleted > 0) {
          addNotification({
            type: NotificationType.INFO,
            title: `${PLAYER_COLOR_DISPLAY[currentPlayer].emoji} ${PLAYER_COLOR_DISPLAY[currentPlayer].name}`,
            message: `Completed ${boxesCompleted} box${boxesCompleted > 1 ? 'es' : ''}! Extra turn!`,
            duration: 1500,
          });
        }

        // Check if game is over
        if (engine.isGameOver()) {
          handleGameOver(newState);
          return;
        }

        // Auto-save
        const currentSettings = settingsRef.current;
        if (persistenceRef.current && currentSettings) {
          persistenceRef.current.autoSave(currentSettings.mode, newState, {
            aiDifficulty: currentSettings.aiDifficulty,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // If next player is AI, schedule move
        if (
          currentSettings?.mode === 'singleplayer' &&
          aiRef.current &&
          engine.getCurrentPlayerIndex() !== 0 &&
          !engine.isGameOver()
        ) {
          scheduleAIMove();
        }
      } catch (error) {
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification]
  );

  const scheduleAIMove = useCallback(() => {
    const engine = engineRef.current;
    if (!aiRef.current || engine.isGameOver()) return;

    const state = engine.getGameState();
    const validMoves = engine.getValidMoves();

    setTimeout(() => {
      if (engine.isGameOver()) return;

      const move = aiRef.current?.chooseMove(state.board, state.gridSize, validMoves);
      if (move) {
        handleAIMove(move);
      }
    }, aiSpeedDelayRef.current);
  }, []);

  const handleAIMove = useCallback(
    (line: Line) => {
      const engine = engineRef.current;
      if (engine.isGameOver()) return;

      try {
        const currentPlayer = engine.getCurrentPlayer();
        const boxesCompleted = engine.drawLine(line);
        const newState = engine.getGameState();
        setGameState(newState);

        if (boxesCompleted > 0) {
          addNotification({
            type: NotificationType.INFO,
            title: `${PLAYER_COLOR_DISPLAY[currentPlayer].emoji} AI`,
            message: `Completed ${boxesCompleted} box${boxesCompleted > 1 ? 'es' : ''}! Extra turn!`,
            duration: 1500,
          });
        }

        if (engine.isGameOver()) {
          handleGameOver(newState);
          return;
        }

        // Auto-save
        const currentSettings = settingsRef.current;
        if (persistenceRef.current && currentSettings) {
          persistenceRef.current.autoSave(currentSettings.mode, newState, {
            aiDifficulty: currentSettings.aiDifficulty,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // If AI still has the turn (completed a box), schedule another move
        if (engine.getCurrentPlayerIndex() !== 0 && !engine.isGameOver()) {
          scheduleAIMove();
        }
      } catch (error) {
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'AI move failed'
        );
      }
    },
    [addNotification, scheduleAIMove]
  );

  const handleGameOver = useCallback(
    (state: DotsBoxesGameState) => {
      setGameState(state);
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      const currentSettings = settingsRef.current;
      if (statsServiceRef.current && currentSettings) {
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: currentSettings.mode,
            difficulty: currentSettings.aiDifficulty,
            gridSize: currentSettings.gridSize,
            playerCount: currentSettings.playerCount,
            winner: state.winner,
            scores: state.scores,
            totalMoves: state.moveHistory.length,
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          'blue', // Human is always blue (player 0)
          isAuthenticated ? user?.id : undefined
        );
      }

      // Show result notification
      const winner = state.winner;
      if (winner === 'draw') {
        addNotification({
          type: NotificationType.INFO,
          title: '🤝 Draw!',
          message: 'Game ended in a draw!',
          duration: 5000,
        });
      } else if (winner) {
        const display = PLAYER_COLOR_DISPLAY[winner];
        addNotification({
          type: NotificationType.SUCCESS,
          title: '🎉 Game Over!',
          message: `${display.emoji} ${display.name} wins with ${state.scores[winner]} boxes!`,
          duration: 5000,
        });
      }
    },
    [addNotification, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    settingsRef.current = null;
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // Trigger AI on initial turn if AI goes first (shouldn't happen in dots-and-boxes, but safe)
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    const engine = engineRef.current;
    if (
      engine.getCurrentPlayerIndex() !== 0 &&
      !engine.isGameOver()
    ) {
      scheduleAIMove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const dotsConfig: GameModeConfig = {
      gameName: 'Dots & Boxes',
      gameIcon: '📦',
      subtitle: 'Connect the dots',
      availableModes: ['ai', 'local', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: (selection.difficulty as any) || 'medium',
          gridSize: 4,
          playerCount: 2,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({
          mode: 'local-multiplayer',
          gridSize: 4,
          playerCount: 2,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', gridSize: 4, playerCount: 2 });
      }
    };

    return (
      <div className="dots-game">
        <div className="dots-game__header">
          <h2 className="dots-game__title">⬜ Dots & Boxes</h2>
        </div>
        <UniversalGameModeSelector
          config={dotsConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isHumanTurn =
    settings?.mode !== 'singleplayer' || gameState.currentPlayerIndex === 0;
  const isBoardDisabled = !isHumanTurn || gameState.gameStatus === 'completed';

  // Determine turn text
  const turnText = gameState.gameStatus === 'completed'
    ? 'Game Over'
    : !isHumanTurn
      ? 'AI thinking...'
      : `${PLAYER_COLOR_DISPLAY[currentPlayer].emoji} ${PLAYER_COLOR_DISPLAY[currentPlayer].name}'s turn`;

  // Left panels: Score display
  const leftPanels = [
    {
      id: 'scores',
      title: 'Scores',
      icon: '📊',
      content: (
        <div className="dots-game__score-bar" role="status" aria-live="polite">
          {gameState.players.map((player, index) => {
            const display = PLAYER_COLOR_DISPLAY[player];
            const isActive = index === gameState.currentPlayerIndex;
            return (
              <div
                key={player}
                className={`dots-game__score dots-game__score--${player} ${isActive ? 'dots-game__score--active' : ''}`}
              >
                <span className="dots-game__score-emoji" aria-hidden="true">
                  {display.emoji}
                </span>
                <span className="dots-game__score-label">
                  {settings?.mode === 'singleplayer' && index > 0 ? 'AI' : display.name}
                </span>
                <span className="dots-game__score-value">{gameState.scores[player]}</span>
              </div>
            );
          })}
        </div>
      ),
    },
  ];

  // Game over config
  const gameOverConfig = view === 'game-over' && gameState.gameStatus === 'completed' ? {
    show: true,
    result: (gameState.winner === 'draw' ? 'draw'
      : (gameState.winner === 'blue' ? 'win' : 'loss')) as 'win' | 'loss' | 'draw',
    title: gameState.winner === 'draw' ? 'Draw!' : 'Game Over!',
    subtitle: gameState.winner && gameState.winner !== 'draw'
      ? `${settings?.mode === 'singleplayer' && gameState.winner !== 'blue' ? 'AI' : PLAYER_COLOR_DISPLAY[gameState.winner].name} wins! | ${gameState.moveHistory.length} moves`
      : `${gameState.moveHistory.length} moves`,
    icon: gameState.winner === 'draw' ? '🤝' : (gameState.winner === 'blue' ? '🎉' : '😞'),
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  // Secondary actions: AI speed slider
  const secondaryActions = settings?.mode === 'singleplayer' ? [
    <AISpeedSlider key="ai-speed" value={aiSpeedDelay} onChange={handleAiSpeedChange} />,
  ] : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Dots & Boxes',
    board: (
      <DotsBoxesBoard
        board={gameState.board}
        gridSize={gameState.gridSize}
        currentPlayer={currentPlayer}
        onLineClick={handleLineClick}
        disabled={isBoardDisabled}
      />
    ),
    leftPanels,
    statusBar: {
      turnIndicator: <span>{turnText}</span>,
      gameStatus: gameState.gameStatus === 'completed' ? 'Game Over' : `Lines: ${gameState.drawnLines}/${gameState.totalLines}`,
      gameStatusType: gameState.gameStatus === 'completed' ? 'ended' : 'active',
      aiThinking: !isHumanTurn && gameState.gameStatus !== 'completed',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    secondaryActions,
    gameOver: gameOverConfig,
  };

  return (
    <div className="dots-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const DotsBoxesGame: React.FC<DotsBoxesGameProps> = (props) => (
  <ErrorBoundary componentName="DotsAndBoxes">
    <DotsBoxesGameInner {...props} />
  </ErrorBoundary>
);

export { DotsBoxesGame };
export default DotsBoxesGame;
